import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@nexus/ui";
import type { AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import {
  inviteRoleOptionsForOrg,
  orgHasInviteRole,
  type InviteWildcardTemplate,
} from "@/features/identity-hub/lib/invite-role-options";
import { ORG_ACCENT_ROW_WASH_CLASS, orgAccentVar } from "@/features/identity-hub/utils/org-accent-surface";
import { OrgLogoMark } from "./OrgLogoMark";
import { OrgPickerCombobox, type OrgPickerItem } from "./OrgPickerCombobox";

/** One selected organization and the role to grant in it. */
export interface OrgRoleSelection {
  orgId: string;
  /** Empty until the user picks a role for this org. */
  accessId: string;
}

interface InviteOrgRoleListProps {
  orgs: OrgPickerItem[];
  accessGroups: AccessGroupItem[];
  /**
   * Active wildcard catalog roles. Passed only for a platform superadmin so
   * roles that were never projected onto the org still appear in the picker.
   */
  wildcardTemplates?: InviteWildcardTemplate[];
  value: OrgRoleSelection[];
  onChange: (next: OrgRoleSelection[]) => void;
}

/**
 * Multi-organization picker for the invite flow. The user adds organizations
 * one at a time; each selected org becomes a row with its own role selector,
 * so a single invite can target several orgs with a distinct role per org.
 *
 * Follows the repo's multi-select convention (see IndustryMultiSelect): an
 * add-control on top, removable rows below. Roles are filtered to the org's
 * own access groups, mirroring the previous single-org behaviour.
 */
export function InviteOrgRoleList({
  orgs,
  accessGroups,
  wildcardTemplates = [],
  value,
  onChange,
}: InviteOrgRoleListProps) {
  const { t } = useTranslation();
  const grantableKeys = useGrantablePermissionTupleKeys();

  const selectedIds = new Set(value.map((s) => s.orgId));
  // Only orgs the current user can actually grant a role in are invitable —
  // an org with no visible roles can't be a valid invite target, so it's
  // hidden from the picker (e.g. a TPJ admin never sees Publicis). A platform
  // superadmin can also invite onto an org that only has unprojected wildcards.
  const invitableOrgIds = new Set(
    orgs
      .filter((org) => orgHasInviteRole(accessGroups, org.id, grantableKeys, wildcardTemplates))
      .map((org) => org.id),
  );
  const availableOrgs = orgs.filter((o) => !selectedIds.has(o.id) && invitableOrgIds.has(o.id));
  const orgById = new Map(orgs.map((o) => [o.id, o]));

  // Auto-pick the role for any selected org that offers exactly one, so the
  // user doesn't have to confirm a choice with no alternatives.
  useEffect(() => {
    let changed = false;
    const next = value.map((sel) => {
      if (sel.accessId) return sel;
      const roles = inviteRoleOptionsForOrg(accessGroups, sel.orgId, grantableKeys, wildcardTemplates);
      if (roles.length !== 1) return sel;
      changed = true;
      return { ...sel, accessId: roles[0].id };
    });
    if (changed) onChange(next);
  }, [value, accessGroups, onChange, grantableKeys, wildcardTemplates]);

  const addOrg = (orgId: string) => {
    if (!orgId || selectedIds.has(orgId)) return;
    onChange([...value, { orgId, accessId: "" }]);
  };

  const addAll = () => {
    if (availableOrgs.length === 0) return;
    onChange([...value, ...availableOrgs.map((o) => ({ orgId: o.id, accessId: "" }))]);
  };

  const removeOrg = (orgId: string) => {
    onChange(value.filter((s) => s.orgId !== orgId));
  };

  const clearAll = () => onChange([]);

  const setRole = (orgId: string, accessId: string) => {
    onChange(value.map((s) => (s.orgId === orgId ? { ...s, accessId } : s)));
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Add-organization control. Stays open on select so several orgs can be
          added in one pass, with a "Select all" shortcut pinned below the list. */}
      <OrgPickerCombobox
        items={availableOrgs}
        value=""
        onValueChange={addOrg}
        placeholder={t("iam.inviteDialog.addOrgPlaceholder")}
        searchPlaceholder={t("iam.personDrawer.orgs.searchOrgs")}
        ariaLabel={t("iam.inviteDialog.addToOrgs")}
        disabled={availableOrgs.length === 0}
        closeOnSelect={false}
        contentClassName="w-[22rem] max-w-[calc(100vw-3rem)]"
        footer={
          availableOrgs.length > 1 ? (
            <button
              type="button"
              onClick={addAll}
              className="flex w-full items-center justify-center px-3 py-2 text-[12px] font-medium text-primary transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t("iam.inviteDialog.selectAllOrgs", { count: availableOrgs.length })}
            </button>
          ) : undefined
        }
      />

      {/* Count + clear-all — surfaced once the list is long enough to need it */}
      {value.length > 1 && (
        <div className="flex items-center justify-between px-0.5">
          <span className="text-[11px] font-medium text-muted-foreground" aria-live="polite">
            {t("iam.inviteDialog.orgsSelectedMultiple", { count: value.length })}
          </span>
          <button
            type="button"
            onClick={clearAll}
            className="rounded text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("iam.inviteDialog.clearAllOrgs")}
          </button>
        </div>
      )}

      {/* Selected org rows — one role selector per org. Caps height and scrolls
          internally so a long org list never pushes the rest of the form away. */}
      {value.length > 0 && (
        <ul
          aria-label={t("iam.inviteDialog.addToOrgs")}
          className="flex max-h-[14.5rem] flex-col gap-1.5 overflow-y-auto overscroll-contain pr-0.5"
        >
          {value.map((sel) => {
            const org = orgById.get(sel.orgId);
            const accent = org ? tenantAccentColor(org) : undefined;
            const groupsForOrg = inviteRoleOptionsForOrg(
              accessGroups,
              sel.orgId,
              grantableKeys,
              wildcardTemplates,
            );
            return (
              <li
                key={sel.orgId}
                style={accent ? orgAccentVar(accent) : undefined}
                className={cn("flex items-center gap-2.5 rounded-md px-2.5 py-1.5", accent && ORG_ACCENT_ROW_WASH_CLASS)}
              >
                <OrgLogoMark
                  name={org?.name ?? sel.orgId}
                  logoUrl={org?.logoUrl}
                  accent={accent}
                  isSuperTenant={org?.type === "SUPER_TENANT"}
                  size={20}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {org?.name ?? sel.orgId}
                </span>
                <div className="relative shrink-0">
                  <select
                    value={sel.accessId}
                    onChange={(e) => setRole(sel.orgId, e.target.value)}
                    aria-label={t("iam.inviteDialog.roleForOrg", { org: org?.name ?? sel.orgId })}
                    className="h-7 w-[180px] appearance-none rounded-md border border-border bg-background pl-2.5 pr-8 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40"
                  >
                    <option value="" disabled>
                      {groupsForOrg.length === 0
                        ? t("iam.inviteDialog.noGrantableRoles", "No roles you can grant")
                        : t("iam.inviteDialog.selectGroup")}
                    </option>
                    {groupsForOrg.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                </div>
                <button
                  type="button"
                  aria-label={t("iam.inviteDialog.removeOrg", { org: org?.name ?? sel.orgId })}
                  onClick={() => removeOrg(sel.orgId)}
                  // Compact icon, but an invisible inset expander grows the hit
                  // target to ~44px without bloating the dense row height.
                  className="relative shrink-0 rounded-md p-2 text-muted-foreground transition-colors after:absolute after:inset-[-6px] after:content-[''] hover:bg-destructive/10 hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
